import type { ComponentProps } from "react"
import { cn } from "@/utilities"

const ORDER_STATUS_LABELS: Record<string, string> = {
  cancelled: "Cancelado",
  completed: "Concluído",
  failed: "Falhou",
  "on-hold": "Em espera",
  pending: "Pendente",
  processing: "Processando",
  refunded: "Reembolsado",
}

const ORDER_STATUS_COLORS: Record<string, string> = {
  cancelled: "bg-red-50 text-red-600",
  completed: "bg-green-50 text-green-600",
  failed: "bg-red-50 text-red-600",
  "on-hold": "bg-orange-50 text-orange-600",
  pending: "bg-yellow-50 text-yellow-600",
  processing: "bg-blue-50 text-blue-600",
  refunded: "bg-purple-50 text-purple-600",
}

function formatOrderDate(value: string): string {
  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return value || "-"
  }

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date)
}

function toOrderStatusLabel(status: string): string {
  return ORDER_STATUS_LABELS[status] ?? status
}

interface OrderSummaryProps extends ComponentProps<"div"> {
  orderNumber: string
  dateCreated: string
  status: string
  total: string
}

export function OrderSummary({
  orderNumber,
  dateCreated,
  status,
  total,
  className,
  ...props
}: OrderSummaryProps) {
  const statusLabel = toOrderStatusLabel(status)
  const formattedDate = formatOrderDate(dateCreated)
  const statusColor = ORDER_STATUS_COLORS[status] ?? "bg-muted text-foreground"

  return (
    <div
      className={cn(
        "space-y-4 rounded-2xl border border-border bg-background p-6 shadow-xs",
        className
      )}
      {...props}
    >
      <div className='space-y-2'>
        <p className='text-muted-foreground text-sm uppercase tracking-wide'>
          Número do pedido
        </p>
        <p className='font-semibold text-2xl text-foreground'>{orderNumber}</p>
      </div>

      <div className='space-y-2'>
        <p className='text-muted-foreground text-sm uppercase tracking-wide'>
          Data do pedido
        </p>
        <p className='text-foreground'>{formattedDate}</p>
      </div>

      <div className='space-y-2'>
        <p className='text-muted-foreground text-sm uppercase tracking-wide'>
          Status
        </p>
        <span
          className={cn(
            "inline-block rounded-md px-3 py-1 font-medium text-sm",
            statusColor
          )}
        >
          {statusLabel}
        </span>
      </div>

      <div className='border-border border-t pt-4'>
        <div className='flex items-center justify-between'>
          <p className='font-semibold text-foreground text-lg'>Total</p>
          <p className='font-semibold text-2xl text-foreground'>{total}</p>
        </div>
      </div>
    </div>
  )
}
